import { cn } from '@/lib/cn'
import { Plus } from 'lucide-react'
import { useSavedFilters } from '../../hooks/useSavedFilters'
import type { SavedFilter } from '../../types'

interface Props {
  activeId: string | null
  onSelect: (filter: SavedFilter | null) => void
  onCreate: () => void
}

export function SavedFilterTabs({ activeId, onSelect, onCreate }: Props) {
  const { data: saved = [] } = useSavedFilters()
  return (
    // Tablist + the "save current filters" trigger sit side by
    // side visually but in two separate ARIA containers — tablist
    // children must all be `tab`s per WAI-ARIA, so the create
    // button can't live inside it.
    <div className="flex items-center gap-1 border-b border-border">
      <div role="tablist" aria-label="Saved alert filters" className="flex items-center gap-1">
        <Tab
          active={activeId === null}
          onClick={() => onSelect(null)}
          label="All open"
          count={null}
        />
        {saved.map((f) => (
          <Tab
            key={f.id}
            active={activeId === f.id}
            onClick={() => onSelect(f)}
            label={f.name}
            count={f.count}
          />
        ))}
      </div>
      <button
        type="button"
        onClick={onCreate}
        className="ml-1 flex items-center gap-1 rounded px-2 py-1 text-xs text-muted-fg hover:bg-muted hover:text-fg"
        aria-label="Save current filters as a new set"
      >
        <Plus size={12} />
      </button>
    </div>
  )
}

function Tab({
  active,
  onClick,
  label,
  count,
}: {
  active: boolean
  onClick: () => void
  label: string
  count: number | null
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={cn(
        '-mb-px flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm transition-colors',
        active
          ? 'border-primary text-fg font-medium'
          : 'border-transparent text-muted-fg hover:text-fg',
      )}
    >
      {label}
      {count !== null && (
        <span
          className={cn(
            'rounded-full px-1.5 py-0 text-[0.625rem] font-mono tabular-nums',
            active ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-fg',
          )}
        >
          {count}
        </span>
      )}
    </button>
  )
}
